'use client';

import { type AxiosInstance } from 'axios';
import { createContext, useContext } from 'react';

import {
  AnimalSpeciesBreedApi,
  AuthorisedRepresentativeApi,
  BillableEventApi,
  DocumentApi,
  EntityApi,
  EntityDetailApi,
  EntityDetailCategoryApi,
  EntityDetailConfigApi,
  EntityDetailEnhancerApi,
  EntityDetailValueApi,
  EntityTransferApi,
  FeeApi,
  FormActionAssociationApi,
  ImportBatchApi,
  FormApi,
  NoteApi,
  OrganisationApi,
  OrganisationDto,
  OrganisationSettingApi,
  RoleUserOrgAssociationApi,
  RoleUserOrgAssociationDto,
  ScreenApi,
  TimelineApi,
  UserConfigApi,
  WorkOrderApi,
  ValidationApi as ValidationRuleApi,
  RuleExecutionApi,
  RuleConfigurationApi,
  RoleApi,
  SolrSyncApi,
} from '@/apiClient';
import { UserApi, UserProfileApi, type UserProfileDto } from '@/authApiClient';

export interface CoreApiClients {
  entityApi?: EntityApi;
  entityDetailApi?: EntityDetailApi;
  entityDetailCategoryApi?: EntityDetailCategoryApi;
  entityDetailConfigApi?: EntityDetailConfigApi;
  entityDetailEnhancerApi?: EntityDetailEnhancerApi;
  entityDetailValueApi?: EntityDetailValueApi;
  entityTransferApi?: EntityTransferApi;
  animalSpeciesBreedApi?: AnimalSpeciesBreedApi;
  animalDetailApi?: EntityDetailApi;
  documentApi?: DocumentApi;
  noteApi?: NoteApi;
  timelineApi?: TimelineApi;
  importBatchApi?: ImportBatchApi;
  screenApi?: ScreenApi;
}

export interface FormApiClients {
  formApi?: FormApi;
  formActionAssociationApi?: FormActionAssociationApi;
}

export interface OrgApiClients {
  organisationApi?: OrganisationApi;
  organisationSettingApi?: OrganisationSettingApi;
  roleUserOrgAssociationApi?: RoleUserOrgAssociationApi;
  roleApi?: RoleApi;
  solrSyncApi?: SolrSyncApi;
}

export interface AuthApiClients {
  userApi?: UserApi;
  userProfileApi?: UserProfileApi;
  authorisedRepresentativeApi?: AuthorisedRepresentativeApi;
}

export interface RuleApiClients {
  validationRuleApi?: ValidationRuleApi;
  ruleExecutionApi?: RuleExecutionApi;
  ruleConfigurationApi?: RuleConfigurationApi;
}

export interface FinanceApiClients {
  billableEventApi?: BillableEventApi;
  feeApi?: FeeApi;
  workOrderApi?: WorkOrderApi;
  userConfigApi?: UserConfigApi;
}

export type ApiClients = CoreApiClients &
  FormApiClients &
  OrgApiClients &
  AuthApiClients &
  RuleApiClients &
  FinanceApiClients;

export interface UserData {
  userId?: number;
  userGuid?: string;
  userEmail?: string;
  isABRIStaff?: boolean;
  entitlements?: string[];
  userProfile?: UserProfileDto;
  reloadUserProfile?: () => void;
}

export interface OrganisationState {
  selectedOrganisationId?: number;
  setSelectedOrganisationId?: (id: number) => void;
  selectedOrganisation?: OrganisationDto;
  setSelectedOrganisation?: (organisation: OrganisationDto) => void;
  userOrganisations?: RoleUserOrgAssociationDto[];
  setUserOrganisations?: (organisations: RoleUserOrgAssociationDto[]) => void;
}

export interface PrivateContextData
  extends ApiClients, UserData, OrganisationState {
  axiosInstance?: AxiosInstance;
  loginFlowComplete?: boolean;
  showSplashScreen?: boolean;
  setShowSplashScreen?: (show: boolean) => void;
}

const PrivateContext = createContext<PrivateContextData>({});

export const usePrivate = () => {
  return useContext(PrivateContext);
};

export const useApi = (): ApiClients => {
  const ctx = useContext(PrivateContext);
  return {
    entityApi: ctx.entityApi,
    entityDetailApi: ctx.entityDetailApi,
    entityDetailCategoryApi: ctx.entityDetailCategoryApi,
    entityDetailConfigApi: ctx.entityDetailConfigApi,
    entityDetailEnhancerApi: ctx.entityDetailEnhancerApi,
    entityDetailValueApi: ctx.entityDetailValueApi,
    entityTransferApi: ctx.entityTransferApi,
    animalSpeciesBreedApi: ctx.animalSpeciesBreedApi,
    animalDetailApi: ctx.animalDetailApi,
    documentApi: ctx.documentApi,
    noteApi: ctx.noteApi,
    timelineApi: ctx.timelineApi,
    importBatchApi: ctx.importBatchApi,
    screenApi: ctx.screenApi,
    formApi: ctx.formApi,
    formActionAssociationApi: ctx.formActionAssociationApi,
    organisationApi: ctx.organisationApi,
    organisationSettingApi: ctx.organisationSettingApi,
    roleUserOrgAssociationApi: ctx.roleUserOrgAssociationApi,
    roleApi: ctx.roleApi,
    solrSyncApi: ctx.solrSyncApi,
    userApi: ctx.userApi,
    userProfileApi: ctx.userProfileApi,
    authorisedRepresentativeApi: ctx.authorisedRepresentativeApi,
    validationRuleApi: ctx.validationRuleApi,
    ruleExecutionApi: ctx.ruleExecutionApi,
    ruleConfigurationApi: ctx.ruleConfigurationApi,
    billableEventApi: ctx.billableEventApi,
    feeApi: ctx.feeApi,
    workOrderApi: ctx.workOrderApi,
    userConfigApi: ctx.userConfigApi,
  };
};

export const PrivateContextProvider = PrivateContext.Provider;
